// Link Reviews Service
// The owner's link-library review queue, link-editing revocations and churn thresholds (ITER-139).
// Reads are owner-only in the rules; every change goes through an owner callable.

import { collection, doc, getDoc, onSnapshot, query, where, type DocumentData, type Unsubscribe } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import type { LinkProvider } from '../utils/songLinks';

export type LinkReviewType = 'flag' | 'revoked_change' | 'churn' | 'glitch';
export type LinkReviewStatus = 'open' | 'approved' | 'dismissed' | 'deleted';
export type ChurnRule = 'flip_back' | 'too_many_changes' | 'never_settles' | 'report_pile_up';
export type LinkReviewAction = 'dismiss' | 'delete_link' | 'approve_change';
export type LinkEditingEmailStatus = 'sent' | 'skipped' | 'failed';

export interface ProposedLink {
  trackId: string;
  url: string;
}

export interface LinkReview {
  id: string;
  type: LinkReviewType;
  status: LinkReviewStatus;
  songKey: string;
  title: string;
  artist: string;
  provider: LinkProvider;
  trackId: string | null;
  historyId: string | null;
  churnRule: ChurnRule | null;
  detail: string | null;
  proposed: ProposedLink | null;
  requestId: string | null;
  eventId: string | null;
  reason: string | null;
  raisedBy: string;
  raisedByEmail: string | null;
  count: number;
  createdAt: string;
  updatedAt: string;
}

export interface ChurnThresholds {
  flipBackAlerts: boolean;
  tooManyChanges: number;
  neverSettlesStreak: number;
  reportPileUp: number;
}

export const DEFAULT_CHURN_THRESHOLDS: ChurnThresholds = {
  flipBackAlerts: true,
  tooManyChanges: 3,
  neverSettlesStreak: 3,
  reportPileUp: 2,
};

const REVIEW_TYPES: LinkReviewType[] = ['flag', 'revoked_change', 'churn', 'glitch'];
const REVIEW_STATUSES: LinkReviewStatus[] = ['open', 'approved', 'dismissed', 'deleted'];
const CHURN_RULES: ChurnRule[] = ['flip_back', 'too_many_changes', 'never_settles', 'report_pile_up'];
const PROVIDERS: LinkProvider[] = ['spotify', 'tidal', 'apple'];

function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value ? value : null;
}

function isoOf(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value && typeof (value as { toDate?: unknown }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate().toISOString();
  }
  return '';
}

export function docToLinkReview(id: string, data: DocumentData): LinkReview | null {
  if (!REVIEW_TYPES.includes(data.type) || !PROVIDERS.includes(data.provider) || typeof data.songKey !== 'string') return null;
  const proposed = data.proposed && typeof data.proposed.trackId === 'string' && typeof data.proposed.url === 'string'
    ? { trackId: data.proposed.trackId, url: data.proposed.url }
    : null;
  return {
    id,
    type: data.type,
    status: REVIEW_STATUSES.includes(data.status) ? data.status : 'open',
    songKey: data.songKey,
    title: typeof data.title === 'string' ? data.title : '',
    artist: typeof data.artist === 'string' ? data.artist : '',
    provider: data.provider,
    trackId: stringOrNull(data.trackId),
    historyId: stringOrNull(data.historyId),
    churnRule: CHURN_RULES.includes(data.churnRule) ? data.churnRule : null,
    detail: stringOrNull(data.detail),
    proposed,
    requestId: stringOrNull(data.requestId),
    eventId: stringOrNull(data.eventId),
    reason: stringOrNull(data.reason),
    raisedBy: typeof data.raisedBy === 'string' ? data.raisedBy : '',
    raisedByEmail: stringOrNull(data.raisedByEmail),
    count: typeof data.count === 'number' && data.count > 0 ? data.count : 1,
    createdAt: isoOf(data.createdAt),
    updatedAt: isoOf(data.updatedAt),
  };
}

export function subscribeToOpenLinkReviews(
  onChange: (reviews: LinkReview[]) => void,
  onError: (error: Error) => void,
): Unsubscribe {
  const openQuery = query(collection(db, 'linkReviews'), where('status', '==', 'open'));
  return onSnapshot(
    openQuery,
    (snapshot) => onChange(
      snapshot.docs
        .map(docSnap => docToLinkReview(docSnap.id, docSnap.data()))
        .filter((review): review is LinkReview => review !== null)
        .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)),
    ),
    onError,
  );
}

export async function resolveLinkReview(reviewId: string, action: LinkReviewAction): Promise<{ status: string }> {
  const callable = httpsCallable<{ reviewId: string; action: LinkReviewAction }, { status: string }>(functions, 'resolveLinkReview');
  const result = await callable({ reviewId, action });
  return result.data;
}

export async function setLinkEditingRevoked(
  uid: string,
  revoked: boolean,
  note?: string,
): Promise<{ revoked: boolean; already?: boolean; emailStatus?: LinkEditingEmailStatus }> {
  const callable = httpsCallable<
    { uid: string; revoked: boolean; note?: string },
    { revoked: boolean; already?: boolean; emailStatus?: LinkEditingEmailStatus }
  >(functions, 'setLinkEditingRevoked');
  const trimmed = note?.trim();
  const result = await callable({ uid, revoked, ...(trimmed ? { note: trimmed } : {}) });
  return result.data;
}

function thresholdOf(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= 20 ? value : fallback;
}

export async function getLinkChurnSettings(): Promise<ChurnThresholds> {
  const snap = await getDoc(doc(db, 'adminSettings', 'linkChurn'));
  const data = snap.data() ?? {};
  return {
    flipBackAlerts: typeof data.flipBackAlerts === 'boolean' ? data.flipBackAlerts : DEFAULT_CHURN_THRESHOLDS.flipBackAlerts,
    tooManyChanges: thresholdOf(data.tooManyChanges, DEFAULT_CHURN_THRESHOLDS.tooManyChanges),
    neverSettlesStreak: thresholdOf(data.neverSettlesStreak, DEFAULT_CHURN_THRESHOLDS.neverSettlesStreak),
    reportPileUp: thresholdOf(data.reportPileUp, DEFAULT_CHURN_THRESHOLDS.reportPileUp),
  };
}

export async function updateLinkChurnSettings(thresholds: ChurnThresholds): Promise<ChurnThresholds> {
  const callable = httpsCallable<ChurnThresholds, ChurnThresholds>(functions, 'updateLinkChurnSettings');
  const result = await callable(thresholds);
  return result.data;
}
